/**
 * Cena de animação de combate lateral estilo Fire Emblem (GBA).
 *
 * Exibe atacante e defensor com os battlers reais do acervo (sv_actors / sv_enemies),
 * cenário de fundo (battlebacks), barras de vida animadas, números de dano flutuantes,
 * efeitos sonoros e suporte para pular a qualquer instante com Espaço ou Esc.
 */

import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { CONDITIONS, type ConditionId, type Forecast, type Unit, type WeaponDef } from '@oz/rules';
import { ASSETS_BASE, effectUrl, type Manifest } from '../assets/manifest.js';
import { actorStageBox, enemyStageBox, stageUnits } from '../render/battler-stage.js';
import { stageEffectPlayer, type EffectPlayer, type StageSide } from '../assets/effekseer.js';
import {
  attackMotion,
  isLooping,
  motionCycle,
  type MotionName,
} from '../render/battler-motion.js';
import type { GameAudio } from '../game/audio.js';
import type { UnitPresentation } from '../game/setup.js';
import { useGamepad } from '../game/useGamepad.js';
import { ItemIcon } from '../components/ItemIcon.js';

export interface CombatBlow {
  readonly side: 'atacante' | 'defensor';
  readonly damage: number;
  readonly result: 'acerto' | 'critico' | 'erro';
  /** O que este golpe deixou em quem apanhou — anunciado junto do dano. */
  readonly inflicted?: readonly ConditionId[] | undefined;
}

export interface CombatSceneProps {
  readonly manifest: Manifest;
  readonly attacker: Unit;
  readonly defender: Unit;
  readonly attackerPres: UnitPresentation;
  readonly defenderPres: UnitPresentation;
  readonly attackerWeapon: WeaponDef | null;
  readonly defenderWeapon: WeaponDef | null;
  readonly forecast?: Forecast | null;
  readonly blows: readonly CombatBlow[];
  readonly audio: GameAudio | null;
  readonly terrainType?: string | undefined;
  /** Cenário próprio do mapa: chão e horizonte, nos nomes do acervo. */
  readonly battleback?: string | undefined;
  readonly battleback2?: string | undefined;
  readonly onComplete: () => void;
}

/**
 * O compasso da cena, em milissegundos.
 *
 * Os tempos anteriores eram uns 40% mais curtos e a cena passava antes de dar
 * para ler: o número de dano aparecia e sumia junto com a pose, e num embate de
 * dois golpes o modal inteiro durava menos de três segundos e meio. Cada valor
 * aqui é uma pausa que o olho usa — e quem não quiser esperar continua tendo o
 * botão Pular, que existe justamente para isso.
 */
const BEAT = {
  /** Antes do primeiro golpe: o palco entra e o jogador reconhece quem é quem. */
  abertura: 550,
  /** Do avanço até o impacto. */
  investida: 420,
  /** O impacto na tela: dano flutuante, efeito e barra caindo. */
  impacto: 660,
  /** Entre um golpe e o seguinte. */
  entreGolpes: 560,
  /** O cambaleio antes do corpo tocar o chão. */
  tombo: 420,
  /** Depois de alguém cair. */
  desfecho: 1400,
  /** Depois do último golpe, com os dois de pé. */
  encerramento: 1250,
  /** Quando não houve golpe nenhum a mostrar. */
  semGolpes: 1100,
  /** Quanto o efeito visual de impacto fica na tela. */
  efeito: 520,
} as const;

function getWeaponEffect(weapon: WeaponDef | null, isCrit: boolean): string {
  if (!weapon) return isCrit ? 'HitSpecial1' : 'HitPhysical';

  const name = weapon.name.toLowerCase();
  const type = weapon.type;

  // Efeitos mágicos ou elementais pelo nome
  if (name.includes('fogo') || name.includes('chama') || name.includes('explos') || name.includes('flama')) {
    return isCrit ? 'FireAll1' : 'FireOne1';
  }
  if (name.includes('gelo') || name.includes('neve') || name.includes('geada') || name.includes('blizzard')) {
    return isCrit ? 'IceAll1' : 'IceOne1';
  }
  if (name.includes('trov') || name.includes('raio') || name.includes('eletric') || name.includes('relampago')) {
    return isCrit ? 'ThunderAll1' : 'ThunderOne1';
  }
  if (name.includes('luz') || name.includes('sagrad') || name.includes('divin') || name.includes('cura')) {
    return isCrit ? 'LightAll1' : 'LightOne1';
  }
  if (name.includes('treva') || name.includes('sombra') || name.includes('morte')) {
    return isCrit ? 'DarknessAll1' : 'DarknessOne1';
  }

  // Mapeamento por tipo
  switch (type) {
    case 'espada':
    case 'machado':
      return isCrit ? 'SlashSpecial1' : 'SlashPhysical';
    case 'lanca':
      return isCrit ? 'PierceSpecial1' : 'PiercePhysical';
    case 'arco':
      return isCrit ? 'PierceSpecial1' : 'ShootNormal';
    case 'garra':
      return isCrit ? 'ClawSpecial' : 'ClawPhysical';
    case 'cajado':
      return isCrit ? 'BallsOfLight' : 'LightOne1';
    case 'malho':
    case 'luva':
      return isCrit ? 'HitSpecial1' : 'HitPhysical';
    default:
      return isCrit ? 'SlashSpecial1' : 'SlashPhysical';
  }
}

/**
 * Quanto tempo cada quadro de uma pose fica na tela.
 *
 * O acervo troca a cada 12 quadros de um laço de 60 — 200ms. Poses de uma vez
 * só correm mais rápido, porque um golpe é um gesto, não uma espera.
 */
const MOTION_FRAME_MS = { loop: 200, once: 110 } as const;

/**
 * O vaivém dos quadros de uma pose.
 *
 * Pose em laço nunca termina; pose de uma vez só para no último quadro e fica
 * lá — é o que deixa o corpo caído no chão em vez de piscar de volta em pé.
 */
function useMotionFrame(motion: MotionName): number {
  const cycle = motionCycle(motion);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    const looping = isLooping(motion);
    const period = looping ? MOTION_FRAME_MS.loop : MOTION_FRAME_MS.once;
    const total = motionCycle(motion).length;

    const timer = window.setInterval(() => {
      setStep((s) => {
        if (looping) return (s + 1) % total;
        return Math.min(s + 1, total - 1);
      });
    }, period);

    return () => window.clearInterval(timer);
  }, [motion]);

  return cycle[Math.min(step, cycle.length - 1)] ?? cycle[0] ?? 0;
}

/** Renderiza o battler lateral com orientação e alinhamento corretos */
function Battler({
  manifest,
  battlerName,
  isActor,
  side,
  motion,
  isHurt,
  isDead,
}: {
  manifest: Manifest;
  battlerName: string;
  isActor: boolean;
  side: 'left' | 'right';
  motion: MotionName;
  isHurt: boolean;
  isDead: boolean;
}): JSX.Element {
  const frameIndex = useMotionFrame(motion);
  // Dá preferência ao diretório correspondente à unidade
  const actorEntry = manifest.images['sv_actors']?.[battlerName];
  const enemyEntry = manifest.images['sv_enemies']?.[battlerName];
  const primaryEntry = isActor ? (actorEntry ?? enemyEntry) : (enemyEntry ?? actorEntry);

  const hasGrid = Boolean(primaryEntry?.grid);

  // No RPG Maker MZ:
  // - Folhas de sv_actors (hasGrid) olham naturalmente para a ESQUERDA (<-)
  // - Imagens de sv_enemies olham naturalmente para a DIREITA (->)
  // No palco de combate:
  // - O combatente da esquerda deve olhar para a DIREITA (->)
  // - O combatente da direita deve olhar para a ESQUERDA (<-)
  const naturalFacing: 'left' | 'right' = hasGrid ? 'left' : 'right';
  const targetFacing: 'left' | 'right' = side === 'left' ? 'right' : 'left';
  const shouldFlip = naturalFacing !== targetFacing;

  /*
   * Cair não é a mesma coisa dos dois lados do palco.
   *
   * Uma folha de sv_actors tem a pose de tombado desenhada quadro a quadro: o
   * corpo aparece caído, e apagá-lo atrás de um filtro cinza seria esconder
   * justamente o que se quer ver. Já um sv_enemies é uma imagem só, sem pose de
   * morte nenhuma — para ele o acervo desvanece o sprite, e é o que fazemos.
   */
  const imgClass = [
    'battler-img',
    shouldFlip ? 'flip' : '',
    isDead ? (hasGrid ? 'dead dead-actor' : 'dead dead-enemy') : '',
  ]
    .filter(Boolean)
    .join(' ');

  const charScale = isActor ? 0.85 : 1;

  if (hasGrid && primaryEntry?.grid) {
    /*
     * A folha de sv_actors guarda 18 poses de 3 quadros numa grade 9×6; qual
     * quadro corresponde a qual pose está em `battler-motion`, junto com a
     * conta do acervo que o diz.
     *
     * O recorte é em porcentagem, e não em pixels, porque o quadro é medido na
     * altura do palco: em pixels ele ficaria para trás quando o palco encolhe
     * numa janela pequena, e a cena mostraria meio herói e meio vizinho.
     */
    const grid = primaryEntry.grid;
    const columns = Math.max(1, Math.round(primaryEntry.width / grid.frameWidth));
    const rows = Math.max(1, Math.round(primaryEntry.height / grid.frameHeight));
    const column = frameIndex % columns;
    const row = Math.floor(frameIndex / columns);

    const box = actorStageBox(grid.frameWidth);

    return (
      <div className={`battler-wrapper ${isHurt ? 'hurt' : ''} ${isActor ? 'battler-actor' : ''}`}>
        <div
          className={imgClass}
          style={{
            width: stageUnits(box.width * charScale),
            height: stageUnits(box.height * charScale),
            backgroundImage: `url(${ASSETS_BASE}/img/sv_actors/${primaryEntry.file})`,
            backgroundSize: `${columns * 100}% ${rows * 100}%`,
            backgroundPosition: `${columns > 1 ? (column / (columns - 1)) * 100 : 0}% ${
              rows > 1 ? (row / (rows - 1)) * 100 : 0
            }%`,
            imageRendering: isActor ? 'auto' : 'pixelated',
          }}
        />
        <div className="battler-shadow" />
      </div>
    );
  }

  const file = primaryEntry?.file ?? `${battlerName}.webp`;
  const folder = enemyEntry ? 'sv_enemies' : 'sv_actors';
  /*
   * O monstro entra do tamanho que ele é, comprimido contra o battler médio do
   * acervo — e não numa altura fixa igual para todos. Ver `battler-stage`.
   */
  const box = enemyStageBox(primaryEntry?.width ?? 0, primaryEntry?.height ?? 0);

  return (
    <div className={`battler-wrapper ${isHurt ? 'hurt' : ''} ${isActor ? 'battler-actor' : ''}`}>
      <img
        className={imgClass}
        src={`${ASSETS_BASE}/img/${folder}/${file}`}
        alt={battlerName}
        style={{
          width: stageUnits(box.width * charScale),
          height: stageUnits(box.height * charScale),
          objectFit: 'contain',
          imageRendering: isActor ? 'auto' : 'pixelated',
        }}
      />
      <div className="battler-shadow" />
    </div>
  );
}

export function CombatScene({
  manifest,
  attacker,
  defender,
  attackerPres,
  defenderPres,
  attackerWeapon,
  defenderWeapon,
  forecast,
  blows,
  audio,
  terrainType,
  battleback,
  battleback2,
  onComplete,
}: CombatSceneProps): JSX.Element {
  const [attackerHp, setAttackerHp] = useState(attacker.currentHp);
  const [defenderHp, setDefenderHp] = useState(defender.currentHp);

  const attackerItem = attacker.inventory[attacker.equipped];
  const defenderItem = defender.inventory[defender.equipped];
  const attackerForecast = forecast?.attacker;
  const defenderForecast = forecast?.defender;

  const [attackerAttacking, setAttackerAttacking] = useState(false);
  const [defenderAttacking, setDefenderAttacking] = useState(false);
  const [attackerHurt, setAttackerHurt] = useState(false);
  const [defenderHurt, setDefenderHurt] = useState(false);

  /*
   * A pose de cada lado, no vocabulário do acervo.
   *
   * Era aqui que faltava o essencial: a cena tinha um booleano "atacando" e
   * mostrava sempre o mesmo quadro para ele — que, por engano de índice, era
   * `guard`, a pose de defender. Quem ataca agora usa a pose que a arma pede
   * (golpear, estocar, disparar, conjurar), quem apanha usa `damage`, quem
   * desvia usa `evade`, e quem morre cai: `dying` e depois `dead`, que o
   * acervo sempre teve e o jogo nunca mostrou.
   */
  const [attackerMotion, setAttackerMotion] = useState<MotionName>('wait');
  const [defenderMotion, setDefenderMotion] = useState<MotionName>('wait');
  const [floatingDamage, setFloatingDamage] = useState<{
    side: 'atacante' | 'defensor';
    text: string;
    crit: boolean;
  } | null>(null);

  const completedRef = useRef(false);
  const canSkipRef = useRef(false);

  const attackerFxHostRef = useRef<HTMLDivElement>(null);
  const defenderFxHostRef = useRef<HTMLDivElement>(null);
  const attackerFxPlayerRef = useRef<EffectPlayer | null>(null);
  const defenderFxPlayerRef = useRef<EffectPlayer | null>(null);

  const [activeVisualFx, setActiveVisualFx] = useState<{
    side: 'atacante' | 'defensor';
    type: 'slash' | 'pierce' | 'fire' | 'hit';
    crit: boolean;
  } | null>(null);

  /*
   * Os reprodutores Effekseer do palco vivem a página inteira (ver
   * `stageEffectPlayer`): a cena só pendura o canvas de cada lado ao abrir e o
   * solta ao fechar. Criá-los a cada combate foi o que, turno após turno,
   * esgotava o heap do Effekseer e depois os contextos WebGL da página — e o
   * primeiro a cair era o do tabuleiro, deixando o mapa em branco.
   */
  useEffect(() => {
    let cancelled = false;
    const hung: { player: EffectPlayer; host: HTMLDivElement }[] = [];

    const hang = async (
      side: StageSide,
      host: HTMLDivElement | null,
      ref: MutableRefObject<EffectPlayer | null>,
    ): Promise<void> => {
      if (!host) return;
      const player = await stageEffectPlayer(side);
      if (cancelled) return;
      host.replaceChildren(player.canvas);
      ref.current = player;
      hung.push({ player, host });
    };

    Promise.all([
      hang('atacante', attackerFxHostRef.current, attackerFxPlayerRef),
      hang('defensor', defenderFxHostRef.current, defenderFxPlayerRef),
    ]).catch((err) => console.warn('Efeito Effekseer WebGL em modo fallback:', err));

    return () => {
      cancelled = true;
      for (const { player, host } of hung) {
        // Só solta o que ainda está nesta cena: uma cena mais nova pode já ter
        // levado o canvas para a dela.
        if (player.canvas.parentElement !== host) continue;
        player.stop();
        player.canvas.remove();
      }
      attackerFxPlayerRef.current = null;
      defenderFxPlayerRef.current = null;
    };
  }, []);

  // Janela de proteção de 450ms para impedir que o mesmo clique/botão de ataque pule a cena
  useEffect(() => {
    const timer = window.setTimeout(() => {
      canSkipRef.current = true;
    }, 450);
    return () => window.clearTimeout(timer);
  }, []);

  const finish = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    // Parar, e não desfazer: os reprodutores servem ao próximo combate.
    attackerFxPlayerRef.current?.stop();
    defenderFxPlayerRef.current?.stop();
    onComplete();
  };

  const trySkip = () => {
    if (!canSkipRef.current) return;
    finish();
  };

  // Tecla Espaço ou Esc pula a cena de combate após a janela de proteção
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        trySkip();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Gamepad: permite avançar / pular combate usando A (confirma), B (cancela) ou Start
  useGamepad({
    onConfirm: trySkip,
    onCancel: trySkip,
    onSkipCombat: trySkip,
  });

  const triggerCombatFx = (
    targetSide: 'atacante' | 'defensor',
    weapon: WeaponDef | null,
    isCrit: boolean,
  ) => {
    const effectName = getWeaponEffect(weapon, isCrit);
    const player = targetSide === 'defensor' ? defenderFxPlayerRef.current : attackerFxPlayerRef.current;

    // Dispara animação WebGL Effekseer se disponível no acervo
    if (player && manifest.fx?.effects?.[effectName]) {
      try {
        const url = effectUrl(manifest, effectName);
        void player.play(url);
      } catch (e) {
        console.warn('Erro ao carregar efeito Effekseer:', e);
      }
    }

    // Tipo de animação visual de impacto correspondente
    const fxKind = effectName.startsWith('Slash')
      ? 'slash'
      : effectName.startsWith('Pierce') || effectName.startsWith('Shoot')
        ? 'pierce'
        : effectName.startsWith('Fire')
          ? 'fire'
          : 'hit';

    setActiveVisualFx({ side: targetSide, type: fxKind, crit: isCrit });
    window.setTimeout(() => setActiveVisualFx(null), BEAT.efeito);
  };

  // Sequência cinematográfica dos golpes
  useEffect(() => {
    if (blows.length === 0) {
      const timer = window.setTimeout(finish, BEAT.semGolpes);
      return () => window.clearTimeout(timer);
    }

    let timeoutId: number;
    let cancelled = false;

    const playBlow = (index: number) => {
      if (cancelled || completedRef.current) return;

      if (index >= blows.length) {
        timeoutId = window.setTimeout(finish, BEAT.encerramento);
        return;
      }

      const blow = blows[index]!;
      const isAttackerBlow = blow.side === 'atacante';
      const swing = attackMotion(isAttackerBlow ? attackerWeapon : defenderWeapon);

      // 1. O combatente avança para o golpe, na pose que a arma pede
      if (isAttackerBlow) {
        setAttackerAttacking(true);
        setAttackerMotion(swing);
      } else {
        setDefenderAttacking(true);
        setDefenderMotion(swing);
      }

      // 2. Momento do impacto
      timeoutId = window.setTimeout(() => {
        if (cancelled || completedRef.current) return;

        if (blow.result === 'erro') {
          audio?.play('falha');
          // Errar não é apanhar: o alvo desvia, e o acervo tem a pose para isso.
          if (isAttackerBlow) setDefenderMotion('evade');
          else setAttackerMotion('evade');
          setFloatingDamage({
            side: isAttackerBlow ? 'defensor' : 'atacante',
            text: 'ERROU',
            crit: false,
          });
        } else {
          audio?.play(blow.result === 'critico' ? 'critico' : 'acerto');
          const crit = blow.result === 'critico';
          const targetSide = isAttackerBlow ? 'defensor' : 'atacante';
          const weaponUsed = isAttackerBlow ? attackerWeapon : defenderWeapon;

          // Dispara a animação do efeito de corte, estocada ou explosão
          triggerCombatFx(targetSide, weaponUsed, crit);

          const caused = (blow.inflicted ?? [])
            .map((c) => `${CONDITIONS[c].label.toUpperCase()}!`)
            .join(' ');
          setFloatingDamage({
            side: targetSide,
            text: `${crit ? 'CRÍTICO! ' : ''}-${blow.damage}${caused ? ` · ${caused}` : ''}`,
            crit,
          });

          // Aplica dano à vida
          if (isAttackerBlow) {
            setDefenderHurt(true);
            setDefenderMotion('damage');
            setDefenderHp((hp) => Math.max(0, hp - blow.damage));
          } else {
            setAttackerHurt(true);
            setAttackerMotion('damage');
            setAttackerHp((hp) => Math.max(0, hp - blow.damage));
          }
        }

        // 3. Retorno da pose
        timeoutId = window.setTimeout(() => {
          if (cancelled || completedRef.current) return;
          setAttackerAttacking(false);
          setDefenderAttacking(false);
          setAttackerHurt(false);
          setDefenderHurt(false);
          setFloatingDamage(null);

          // Verifica se o alvo morreu
          const targetDied = isAttackerBlow
            ? defenderHp - blow.damage <= 0 && blow.result !== 'erro'
            : attackerHp - blow.damage <= 0 && blow.result !== 'erro';

          if (targetDied) {
            audio?.play('morte');
            // Cai em dois tempos: cambaleia de pé (`dying`) e só então tomba
            // (`dead`). Emendar direto no chão faz o corpo teletransportar-se
            // para deitado — e as duas poses estão na folha desde sempre.
            const fall = isAttackerBlow ? setDefenderMotion : setAttackerMotion;
            const stand = isAttackerBlow ? setAttackerMotion : setDefenderMotion;
            fall('dying');
            stand('wait');
            timeoutId = window.setTimeout(() => {
              if (cancelled || completedRef.current) return;
              fall('dead');
              timeoutId = window.setTimeout(finish, BEAT.desfecho);
            }, BEAT.tombo);
            return;
          }

          // De volta à guarda, os dois de pé, esperando o próximo golpe.
          setAttackerMotion('wait');
          setDefenderMotion('wait');

          // Próximo golpe
          timeoutId = window.setTimeout(() => playBlow(index + 1), BEAT.entreGolpes);
        }, BEAT.impacto);
      }, BEAT.investida);
    };

    // Pequena pausa antes do primeiro golpe
    timeoutId = window.setTimeout(() => playBlow(0), BEAT.abertura);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [blows]);

  // Trilha sonora dedicada para a batalha
  useEffect(() => {
    const isBoss = Boolean(attacker.isCommander || defender.isCommander);
    audio?.playBattleBgm(isBoss);
    return () => {
      audio?.restoreMapBgm();
    };
  }, [audio, attacker.isCommander, defender.isCommander]);

  /*
   * O cenário do combate: o do mapa primeiro, o da casa por cima.
   *
   * Antes ele saía só do nome do terreno — e os mapas do acervo só distinguem
   * planície, muralha e água. O resultado era campina em todo combate, dentro
   * de calabouço e no meio da neve. O mapa sabe onde está (o acervo declara o
   * cenário, e o pipeline completa o que falta); a casa onde o defensor pisa
   * refina isso quando tem o que dizer — uma floresta pintada no editor muda o
   * horizonte sem apagar o chão do mapa.
   */
  let back1 = battleback || 'Grassland';
  let back2 = battleback2 || battleback || 'Grassland';

  const t = (terrainType ?? '').toLowerCase();
  if (t === 'deserto' || t === 'oasis') {
    back1 = 'Desert';
    back2 = 'Desert';
  } else if (t === 'floresta' || t === 'bosque') {
    back2 = 'Forest';
  } else if (t === 'montanha' || t === 'colina' || t === 'pico') {
    back1 = 'Dirt';
    back2 = 'Cliff';
  } else if (t === 'forte' || t === 'castelo') {
    back1 = 'Castle1';
    back2 = 'Castle1';
  } else if (t === 'ruina' || t === 'ruinas') {
    back1 = 'Stone1';
    back2 = 'Ruins1';
  }

  const back1Entry = manifest.images['battlebacks1']?.[back1] ?? manifest.images['battlebacks1']?.['Grassland'];
  const back1Url = back1Entry ? `${ASSETS_BASE}/img/battlebacks1/${back1Entry.file}` : '';

  const back2Entry = manifest.images['battlebacks2']?.[back2] ?? manifest.images['battlebacks2']?.['Grassland'];
  const back2Url = back2Entry ? `${ASSETS_BASE}/img/battlebacks2/${back2Entry.file}` : '';

  return (
    <div className="combat-modal-backdrop" role="dialog" aria-modal="true" aria-label="Animação de combate">
      <div className="combat-scene-container">
        {/* Cenário de fundo em duas camadas: base de terreno (1) e horizonte/detalhes (2) */}
        <div className="combat-background">
          {back1Url && (
            <div
              className="combat-back-layer1"
              style={{ backgroundImage: `url(${back1Url})` }}
            />
          )}
          {back2Url && (
            <div
              className="combat-back-layer2"
              style={{ backgroundImage: `url(${back2Url})` }}
            />
          )}
          <div className="combat-back-vignette" />
        </div>

        {/* Filtro SVG 2xSaI para interpolação e suavização dos personagens */}
        <svg
          className="combat-2xsai-svg"
          aria-hidden="true"
          style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
        >
          <defs>
            <filter id="filter-2xsai" colorInterpolationFilters="sRGB" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="0.4" result="blur" />
              <feConvolveMatrix
                in="blur"
                order="3"
                preserveAlpha="true"
                kernelMatrix="
                  0   -0.45   0
                -0.45  2.8  -0.45
                  0   -0.45   0"
                result="sharpen"
              />
              <feComponentTransfer in="sharpen" result="contrast">
                <feFuncR type="linear" slope="1.06" intercept="-0.02" />
                <feFuncG type="linear" slope="1.06" intercept="-0.02" />
                <feFuncB type="linear" slope="1.06" intercept="-0.02" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode in="contrast" />
              </feMerge>
            </filter>
          </defs>
        </svg>

        {/* Botão de Pular */}
        <button type="button" className="combat-skip-button" onClick={trySkip} title="Pular animação (Start / Espaço)">
          Pular ⏩
        </button>

        {/* Palco dos Combatentes */}
        <div className="combat-stage">
          {/* Lado do Atacante (Esquerda) */}
          <div className={`combatant-col attacker-col ${attackerAttacking ? 'lunging' : ''}`}>
            {floatingDamage?.side === 'atacante' && (
              <div className={`combat-float-damage ${floatingDamage.crit ? 'crit' : ''}`}>
                {floatingDamage.text}
              </div>
            )}
            {activeVisualFx?.side === 'atacante' && (
              <div
                className={`combat-fx-overlay fx-${activeVisualFx.type} ${activeVisualFx.crit ? 'crit' : ''}`}
              />
            )}
            <div ref={attackerFxHostRef} className="combat-fx-host" />
            <Battler
              manifest={manifest}
              battlerName={attackerPres.battler}
              isActor={attacker.team === 'jogador'}
              side="left"
              motion={attackerMotion}
              isHurt={attackerHurt}
              isDead={attackerHp <= 0}
            />
          </div>

          {/* Lado do Defensor (Direita) */}
          <div className={`combatant-col defender-col ${defenderAttacking ? 'lunging' : ''}`}>
            {floatingDamage?.side === 'defensor' && (
              <div className={`combat-float-damage ${floatingDamage.crit ? 'crit' : ''}`}>
                {floatingDamage.text}
              </div>
            )}
            {activeVisualFx?.side === 'defensor' && (
              <div
                className={`combat-fx-overlay fx-${activeVisualFx.type} ${activeVisualFx.crit ? 'crit' : ''}`}
              />
            )}
            <div ref={defenderFxHostRef} className="combat-fx-host" />
            <Battler
              manifest={manifest}
              battlerName={defenderPres.battler}
              isActor={defender.team === 'jogador'}
              side="right"
              motion={defenderMotion}
              isHurt={defenderHurt}
              isDead={defenderHp <= 0}
            />
          </div>
        </div>

        {/* HUD Inferior estilo Fire Emblem GBA */}
        <div className="combat-hud">
          {/* Painel do Atacante */}
          <div className="combat-card attacker-card">
            <div className="combat-card-header">
              <div className="combat-unit-info">
                <span className="unit-name">{attacker.name}</span>
                {attackerForecast?.effective && (
                  <span className="combat-effective-badge" title="Arma eficaz contra este alvo!">
                    ⚡ Eficaz!
                  </span>
                )}
              </div>
              <div className="combat-weapon-info">
                <ItemIcon
                  icon={(attackerWeapon as any)?.icon}
                  iconSheet={(attackerWeapon as any)?.iconSheet}
                  type={attackerWeapon?.type}
                  size="1.25em"
                />
                <span className="weapon-name">{attackerWeapon?.name ?? 'Desarmado'}</span>
                {attackerItem && attackerWeapon && (
                  <span className="weapon-uses">
                    ({attackerItem.usesLeft}/{attackerWeapon.uses})
                  </span>
                )}
                {attackerForecast?.triangle && attackerForecast.triangle !== 'neutro' && (
                  <span
                    className={`combat-tri-badge tri-${attackerForecast.triangle}`}
                    title={attackerForecast.triangle === 'vantagem' ? 'Vantagem do triângulo' : 'Desvantagem do triângulo'}
                  >
                    {attackerForecast.triangle === 'vantagem' ? '▲ Vantagem' : '▼ Desvantagem'}
                  </span>
                )}
              </div>
            </div>

            <div className="hp-container">
              <span className="hp-label">PV</span>
              <div className="hp-track">
                <div
                  className="hp-fill team-attacker"
                  style={{ width: `${Math.max(0, (attackerHp / attacker.stats.hp) * 100)}%` }}
                />
              </div>
              <span className="hp-value">
                {attackerHp} / {attacker.stats.hp}
              </span>
            </div>

            <div className="combat-stats-grid">
              <div className="combat-stat-col">
                <span className="combat-stat-label">DANO</span>
                <span className="combat-stat-value stat-damage">
                  {attackerForecast?.damage ?? '—'}
                  {attackerForecast && attackerForecast.attacks > 1 && (
                    <em className="combat-mult">×2</em>
                  )}
                </span>
              </div>
              <div className="combat-stat-col">
                <span className="combat-stat-label">ACERTO</span>
                <span className="combat-stat-value stat-hit">
                  {attackerForecast ? `${attackerForecast.hit}%` : '—'}
                </span>
              </div>
              <div className="combat-stat-col">
                <span className="combat-stat-label">CRÍTICO</span>
                <span className="combat-stat-value stat-crit">
                  {attackerForecast ? `${attackerForecast.crit}%` : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="combat-vs-badge">VS</div>

          {/* Painel do Defensor */}
          <div className="combat-card defender-card">
            <div className="combat-card-header">
              <div className="combat-unit-info">
                <span className="unit-name">{defender.name}</span>
                {defenderForecast?.effective && (
                  <span className="combat-effective-badge" title="Arma eficaz contra este alvo!">
                    ⚡ Eficaz!
                  </span>
                )}
              </div>
              <div className="combat-weapon-info">
                {defenderForecast?.triangle && defenderForecast.triangle !== 'neutro' && (
                  <span
                    className={`combat-tri-badge tri-${defenderForecast.triangle}`}
                    title={defenderForecast.triangle === 'vantagem' ? 'Vantagem do triângulo' : 'Desvantagem do triângulo'}
                  >
                    {defenderForecast.triangle === 'vantagem' ? '▲ Vantagem' : '▼ Desvantagem'}
                  </span>
                )}
                {defenderItem && defenderWeapon && (
                  <span className="weapon-uses">
                    ({defenderItem.usesLeft}/{defenderWeapon.uses})
                  </span>
                )}
                <span className="weapon-name">{defenderWeapon?.name ?? 'Desarmado'}</span>
                <ItemIcon
                  icon={(defenderWeapon as any)?.icon}
                  iconSheet={(defenderWeapon as any)?.iconSheet}
                  type={defenderWeapon?.type}
                  size="1.25em"
                />
              </div>
            </div>

            <div className="hp-container">
              <span className="hp-label">PV</span>
              <div className="hp-track">
                <div
                  className="hp-fill team-defender"
                  style={{ width: `${Math.max(0, (defenderHp / defender.stats.hp) * 100)}%` }}
                />
              </div>
              <span className="hp-value">
                {defenderHp} / {defender.stats.hp}
              </span>
            </div>

            <div className="combat-stats-grid">
              <div className="combat-stat-col">
                <span className="combat-stat-label">DANO</span>
                <span className="combat-stat-value stat-damage">
                  {defenderForecast ? (defenderForecast.canAttack ? defenderForecast.damage : '—') : '—'}
                  {defenderForecast && defenderForecast.canAttack && defenderForecast.attacks > 1 && (
                    <em className="combat-mult">×2</em>
                  )}
                </span>
              </div>
              <div className="combat-stat-col">
                <span className="combat-stat-label">ACERTO</span>
                <span className="combat-stat-value stat-hit">
                  {defenderForecast
                    ? defenderForecast.canAttack
                      ? `${defenderForecast.hit}%`
                      : '—'
                    : '—'}
                </span>
              </div>
              <div className="combat-stat-col">
                <span className="combat-stat-label">CRÍTICO</span>
                <span className="combat-stat-value stat-crit">
                  {defenderForecast
                    ? defenderForecast.canAttack
                      ? `${defenderForecast.crit}%`
                      : '—'
                    : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
